import { API } from '..';
import { requireTotpParams } from '../../utils/admin-stepup';

const ADS = '/v1/payment-service/ads';
const ADMIN_ADS = '/v1/payment-service/admin/ads';

// Cab Ads Network (Slice 1). Quảng cáo chạy bằng CA NẠP (không phải Ca đào).
export interface AdCreative {
  campaignId: string;
  creativeId: string;
  format: string; // Banner | Native | Video
  title?: string;
  body?: string;
  imageUrl?: string;
  videoUrl?: string;
  landingUrl: string;
  ctaText?: string;
  sponsored?: string;
  serveToken?: string;
}

export type CreatorDistribution = 'Auto' | 'Named' | 'Marketplace';

export interface AdCampaign {
  id: string;
  advertiserId: string;
  adAccountId?: string | null; // tài khoản DN tài trợ (E3); null = quảng cáo cá nhân
  businessName?: string | null; // tên DN tài trợ (khi adAccountId != null)
  name: string;
  objective?: string; // Awareness | Traffic | Leads | Engagement | Followers
  format: string;
  pricingModel: string; // CPC | CPM
  bidCa: number;
  totalBudgetCa: number;
  dailyBudgetCa?: number | null;
  spentCa: number;
  remainingCa: number;
  impressions: number;
  clicks: number;
  ctr: number; // %
  status: string; // Pending | Active | Paused | Rejected | Completed
  rejectReason?: string | null;
  targetDevice?: string | null;
  /** Vị trí đã chọn; rỗng hoặc null = mọi vị trí. */
  targetPlacements?: string[] | null;
  createdAt: string;
  creative?: AdCreative | null;
  fatigueDetectedAt?: string | null;
  baselineCtr?: number | null;
  recentCtr?: number | null;
  frequency?: number | null; // có thể null ở danh sách
  pendingCreative?: AdCreativeVersion | null;
  activeCreative?: AdCreativeVersion | null;
  creatorDistribution?: CreatorDistribution;
  targetCreators?: { userId: string; fullName: string }[];
  optInCount?: number;
}

// Một phiên bản nội dung quảng cáo (mẫu đang chạy / mẫu thay mới chờ duyệt).
export interface AdCreativeVersion {
  id: string;
  title?: string | null;
  body?: string | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
  landingUrl: string;
  ctaText?: string | null;
  reviewStatus: string; // Approved | Pending | Rejected
  rejectReason?: string | null;
  createdAt: string;
}

export interface CreateAdCampaignPayload {
  adAccountId?: string | null; // null = trừ Ca nạp cá nhân; có giá trị = trừ ví VND của DN (E3)
  name: string;
  objective: string;
  format: string;
  pricingModel: string;
  bidCa: number;
  totalBudgetCa: number;
  dailyBudgetCa?: number | null;
  targetCity?: string | null;
  targetGender?: string | null;
  targetAgeMin?: number | null;
  targetAgeMax?: number | null;
  targetCategories?: string[] | null;
  targetDevice?: string | null;
  targetInterests?: string[] | null;   // sở thích/mối quan tâm (đánh đúng tệp)
  targetPlacements?: string[] | null;  // vị trí hiển thị: Feed | Reels | Video | Profile | HomeBanner
  creatorDistribution?: CreatorDistribution;
  targetCreatorIds?: string[];
  title?: string;
  body?: string;
  imageUrl?: string;
  videoUrl?: string;
  landingUrl: string;
  ctaText?: string;
}

export interface DailyStatPoint {
  date: string;
  impressions: number;
  clicks: number;
  spentCa: number;
}
export interface CampaignStats {
  campaignId: string;
  impressions: number;
  clicks: number;
  ctr: number;
  spentCa: number;
  remainingCa: number;
  avgCpcCa: number;
  avgCpmCa: number;
  /** Chiến dịch Video: lượt bỏ qua và xem hết quảng cáo chèn trước video (chỉ thống kê, máy chủ cũ có thể chưa trả). */
  skips?: number;
  completes?: number;
  daily: DailyStatPoint[];
  fatigueDetectedAt?: string | null;
  baselineCtr?: number | null;
  recentCtr?: number | null;
  frequency?: number | null;
  pendingCreative?: AdCreativeVersion | null;
  activeCreative?: AdCreativeVersion | null;
}
export interface ReachEstimate {
  audienceBase: number;
  estimatedReach: number;
  narrowFactor: number;
}
export interface ReachTargetPayload {
  targetCity?: string | null;
  targetGender?: string | null;
  targetAgeMin?: number | null;
  targetAgeMax?: number | null;
  targetDevice?: string | null;
  targetCategories?: string[] | null;
}

// Mục tiêu chiến dịch (để render form + gợi ý CTA).
export const AD_OBJECTIVES = [
  { value: 'Awareness', label: 'Nhận diện thương hiệu', cta: 'Tìm hiểu thêm' },
  { value: 'Traffic', label: 'Tăng truy cập web', cta: 'Truy cập ngay' },
  { value: 'Leads', label: 'Thu thập khách hàng tiềm năng', cta: 'Đăng ký ngay' },
  { value: 'Engagement', label: 'Tăng tương tác', cta: 'Xem thêm' },
  { value: 'Followers', label: 'Tăng người theo dõi', cta: 'Theo dõi' },
];

export const createAdCampaign = (payload: CreateAdCampaignPayload) =>
  API.post<AdCampaign>('/v1/payment-service/ads/campaigns', payload).then((r) => r.data);

export const getMyAdCampaigns = () =>
  API.get<AdCampaign[]>('/v1/payment-service/ads/campaigns').then((r) => r.data);

export const cancelAdCampaign = (campaignId: string) =>
  API.post<AdCampaign>(`/v1/payment-service/ads/campaigns/${campaignId}/cancel`).then((r) => r.data);

export const setAdCampaignPaused = (campaignId: string, paused: boolean) =>
  API.post<AdCampaign>(
    `/v1/payment-service/ads/campaigns/${campaignId}/paused?paused=${paused}`
  ).then((r) => r.data);

export const getCampaignStats = (campaignId: string, days = 14) =>
  API.get<CampaignStats>(
    `/v1/payment-service/ads/campaigns/${campaignId}/stats?days=${days}`
  ).then((r) => r.data);

export const estimateReach = (payload: ReachTargetPayload) =>
  API.post<ReachEstimate>('/v1/payment-service/ads/estimate-reach', payload).then(
    (r) => r.data
  );

// Serve + track (chèn quảng cáo vào feed)
export const serveAds = (body: {
  city?: string;
  gender?: string;
  age?: number;
  categoryId?: string;
  device?: string;
  interests?: string;
  placement?: string;
  count?: number;
  creatorId?: string;
  postId?: string;
}) =>
  API.post<AdCreative[]>('/v1/payment-service/ads/serve', body).then((r) => r.data);

export { AD_PLACEMENTS } from './ad-placements';

// ── Creator kiếm tiền từ quảng cáo ──
export interface CreatorAdsInfo {
  eligible: boolean;
  checks: { isCreator: boolean; ekyc: boolean; donate: boolean };
  adsEnabled: boolean;
  sharePercent: number;
  holdDays: number;
  mode: 'Auto' | 'Manual';
  totals: { creditedCa: number; pendingCa: number; impressions30d: number; clicks30d: number };
  daily: { day: string; impressions: number; clicks: number; shareCa: number }[];
  // Quảng cáo trên trang cá nhân (máy chủ cũ có thể chưa trả, nên để tuỳ chọn).
  /** Creator đủ điều kiện kiếm tiền quảng cáo, không xét công tắc. */
  isCreator?: boolean;
  /** Trang cá nhân của tôi hiện có quảng cáo hay không. */
  profileAdsOn?: boolean;
  /** Được tắt quảng cáo trên trang cá nhân: creator luôn được, người dùng thường cần Cab+ Pro. */
  canTurnOffProfileAds?: boolean;
  proUntil?: string | null;
  /** Cab đang chia doanh thu quảng cáo cho creator (creatorAds.enabled). */
  creatorShareOn?: boolean;
}
/** Mã lỗi 400 khi người dùng thường chưa có Cab+ Pro mà tắt quảng cáo trên trang cá nhân. */
export const PROFILE_ADS_NEED_PRO = 'PROFILE_ADS_NEED_PRO';
export const getCreatorAds = () =>
  API.get<CreatorAdsInfo>('/v1/payment-service/creator-ads/me').then((r) => r.data);
export const setCreatorAdsEnabled = (adsEnabled: boolean) =>
  API.post<CreatorAdsInfo>('/v1/payment-service/creator-ads/me', { adsEnabled }).then((r) => r.data);
export const setCreatorAdsMode = (mode: 'Auto' | 'Manual') =>
  API.post<CreatorAdsInfo>('/v1/payment-service/creator-ads/me', { mode }).then((r) => r.data);
/** Lưu cùng lúc công tắc và chế độ (một lần gọi), dùng cho lựa chọn Tắt / Tự động / Tự chọn đối tác. */
export const saveCreatorAds = (body: { adsEnabled?: boolean; mode?: 'Auto' | 'Manual' }) =>
  API.post<CreatorAdsInfo>('/v1/payment-service/creator-ads/me', body).then((r) => r.data);

export interface CreatorSearchItem {
  userId: string;
  fullName: string;
  userName: string;
  avatar?: string | null;
}
export const searchAdCreators = (q: string) =>
  API.get<CreatorSearchItem[]>('/v1/payment-service/ads/creator-search', { params: { q } }).then((r) => r.data);

export interface MarketplaceAd {
  campaignId: string;
  advertiserName: string;
  title?: string;
  body?: string;
  imageUrl?: string;
  landingDomain?: string;
  objective?: string;
  pricingModel?: string;
  categories: string[];
  optedIn: boolean;
}
export const getAdMarketplace = (page = 1, category?: string) =>
  API.get<{ items: MarketplaceAd[]; total: number }>('/v1/payment-service/creator-ads/marketplace', {
    params: { page, ...(category ? { category } : {}) },
  }).then((r) => r.data);
export const setAdOptIn = (campaignId: string, optedIn: boolean) =>
  API.post('/v1/payment-service/creator-ads/opt-in', { campaignId, optedIn }).then((r) => r.data);

// Nhận diện thiết bị người xem (để serve khớp nhắm mục tiêu thiết bị).
export const detectDevice = (): 'iOS' | 'Android' | 'Web' => {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  return 'Web';
};

export type AdEventType = 'Impression' | 'Click' | 'Skip' | 'Complete';

export const recordAdEvent = (body: {
  campaignId: string;
  creativeId: string;
  /** Impression và Click tính tiền; Skip và Complete (chèn trước video) chỉ để thống kê, máy chủ không tính tiền. */
  type: AdEventType;
  deviceId?: string;
  serveToken?: string;
}) => API.post('/v1/payment-service/ads/event', body).then((r) => r.data);

// Admin
export const adminListAdCampaigns = (status?: string) =>
  API.get<AdCampaign[]>(
    `/v1/payment-service/admin/ads/campaigns${status ? `?status=${status}` : ''}`
  ).then((r) => r.data);

// Máy chủ bắt mã Authenticator (duyệt/từ chối có hoàn Ca) — hỏi mã ngay tại đây.
export const adminReviewAdCampaign = async (
  campaignId: string,
  approve: boolean,
  rejectReason?: string
) => {
  const params = requireTotpParams(approve ? 'duyệt chiến dịch quảng cáo' : 'từ chối chiến dịch quảng cáo');
  return API.post<AdCampaign>(
    `/v1/payment-service/admin/ads/campaigns/${campaignId}/review`,
    { approve, rejectReason },
    { params }
  ).then((r) => r.data);
};

// ── Báo cáo quảng cáo (người xem) ──
export const AD_REPORT_REASONS = [
  { value: 'Spam', label: 'Spam / gây phiền' },
  { value: 'Scam', label: 'Lừa đảo' },
  { value: 'Offensive', label: 'Phản cảm / không phù hợp' },
  { value: 'Misleading', label: 'Sai sự thật' },
  { value: 'Other', label: 'Lý do khác' },
];

export interface AdReportResult {
  reported: boolean;
  autoPaused: boolean;
  totalReports: number;
  message: string;
}
export const reportAd = (body: {
  campaignId: string;
  creativeId?: string;
  reason: string;
  note?: string;
}) => API.post<AdReportResult>('/v1/payment-service/ads/report', body).then((r) => r.data);

// ── Admin xử lý báo cáo ──
export interface ReportedAd {
  campaignId: string;
  name: string;
  advertiserId: string;
  status: string;
  remainingCa: number;
  reportCount: number;
  reasonBreakdown: Record<string, number>;
  lastReportedAt: string;
  creative?: AdCreative | null;
}
export const getReportedAds = () =>
  API.get<ReportedAd[]>('/v1/payment-service/admin/ads/reported').then((r) => r.data);

export const resolveAdReport = (
  campaignId: string,
  action: 'Dismiss' | 'Reject',
  rejectReason?: string
) =>
  API.post<AdCampaign>(
    `/v1/payment-service/admin/ads/campaigns/${campaignId}/resolve-report`,
    { action, rejectReason }
  ).then((r) => r.data);

// ── Ngân sách ngày, luật tự động, nội dung thay mới ──
export const setAdDailyBudget = (campaignId: string, dailyBudgetCa: number | null) =>
  API.put<AdCampaign>(`${ADS}/campaigns/${campaignId}/daily-budget`, { dailyBudgetCa }).then(
    (r) => r.data
  );

export interface AdRule {
  id: string;
  campaignId: string;
  template: string; // PauseHighCpc | PauseLowCtr | ScaleUpBudget | ScaleDownBudget
  windowDays: number;
  threshold: number;
  minImpressions?: number | null;
  changePercent?: number | null;
  enabled: boolean;
  lastTriggeredAt?: string | null;
  createdAt: string;
}
export interface AdRuleBody {
  template: string;
  windowDays: number;
  threshold: number;
  minImpressions?: number | null;
  changePercent?: number | null;
  enabled?: boolean;
}
export interface AdRuleRun {
  id: string;
  ruleId: string;
  template: string;
  action: string; // Paused | BudgetIncreased | BudgetDecreased
  impressions: number;
  clicks: number;
  spentCa: number;
  metricValue: number;
  beforeDailyBudgetCa?: number | null;
  afterDailyBudgetCa?: number | null;
  createdAt: string;
}

export const getAdRules = (campaignId: string) =>
  API.get<AdRule[]>(`${ADS}/campaigns/${campaignId}/rules`).then((r) => r.data);
export const createAdRule = (campaignId: string, body: AdRuleBody) =>
  API.post<AdRule>(`${ADS}/campaigns/${campaignId}/rules`, body).then((r) => r.data);
export const updateAdRule = (campaignId: string, ruleId: string, body: AdRuleBody) =>
  API.put<AdRule>(`${ADS}/campaigns/${campaignId}/rules/${ruleId}`, body).then((r) => r.data);
export const deleteAdRule = (campaignId: string, ruleId: string) =>
  API.delete(`${ADS}/campaigns/${campaignId}/rules/${ruleId}`).then((r) => r.data);
export const getAdRuleRuns = (campaignId: string, take = 50) =>
  API.get<AdRuleRun[]>(`${ADS}/campaigns/${campaignId}/rule-runs?take=${take}`).then((r) => r.data);

export interface AdCreativePayload {
  title?: string;
  body?: string;
  imageUrl?: string;
  videoUrl?: string;
  landingUrl: string;
  ctaText?: string;
}
export const addAdCreative = (campaignId: string, payload: AdCreativePayload) =>
  API.post<AdCreativeVersion>(`${ADS}/campaigns/${campaignId}/creatives`, payload).then(
    (r) => r.data
  );

export interface AdminCreativeReview {
  campaignId: string;
  campaignName: string;
  advertiserId: string;
  campaignStatus: string;
  format: string;
  currentCreative?: AdCreativeVersion | null;
  newCreative: AdCreativeVersion;
}
export const adminListAdCreatives = (status = 'Pending') =>
  API.get<AdminCreativeReview[]>(`${ADMIN_ADS}/creatives?status=${status}`).then((r) => r.data);
export const adminReviewAdCreative = (creativeId: string, approve: boolean, rejectReason?: string) =>
  API.post<AdminCreativeReview>(`${ADMIN_ADS}/creatives/${creativeId}/review`, {
    approve,
    rejectReason,
  }).then((r) => r.data);
